
import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { ProfileForm } from "@/components/ProfileForm";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";

export default function Profile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState(false);

  useEffect(() => {
    async function checkProfile() {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        console.log("Checking profile for user:", user.id);
        
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
          
        if (error) {
          console.error("Error checking profile:", error);
          toast({
            title: "Profile Error",
            description: "Could not check profile status: " + error.message,
            variant: "destructive"
          });
          setHasProfile(false);
        } else {
          if (!data) {
            console.log("No profile found, attempting to create one");
            
            // Try to automatically create a profile with basic data
            const { error: insertError } = await supabase
              .from('user_profiles')
              .insert({
                user_id: user.id,
                name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
                email: user.email || '',
                activity_level: 'low', // Set valid default for activity_level
              });
              
            if (insertError) {
              console.error("Error creating profile:", insertError);
              toast({
                title: "Profile Creation Error",
                description: "Could not create a profile: " + insertError.message,
                variant: "destructive"
              });
            } else {
              console.log("Profile created successfully");
              setHasProfile(true);
            }
          } else {
            console.log("Profile found:", data);
            setHasProfile(!!data);
          }
        }
      } catch (err) {
        console.error("Error in profile check:", err);
        setHasProfile(false);
      } finally {
        setLoading(false);
      }
    }
    
    // Check and ensure Vita credits exist
    async function ensureVitaCredits() {
      if (!user) return;
      
      try {
        console.log("Checking vita credits for user:", user.id);
        
        // Check if credits already exist
        const { data: creditData, error: fetchError } = await supabase
          .from('vita_credits')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
          
        if (fetchError) {
          console.error("Error checking vita credits:", fetchError);
          
          // Attempt to create credits despite error
          const { error: insertError } = await supabase
            .from('vita_credits')
            .insert({
              user_id: user.id,
              credits_limit: 100,
              credits_used: 0
            });
            
          if (insertError) {
            console.error("Error creating vita credits:", insertError);
          } else {
            console.log("Vita credits created after fetch error");
          }
          
          return;
        }
        
        // If no credits found, create them
        if (!creditData) {
          console.log("No vita credits found, creating them");
          
          const { error: insertError } = await supabase
            .from('vita_credits')
            .insert({
              user_id: user.id,
              credits_limit: 100,
              credits_used: 0
            });
            
          if (insertError) {
            console.error("Error creating vita credits:", insertError);
            
            // Try a different approach - no return clause
            const { error: simpleInsertError } = await supabase
              .from('vita_credits')
              .insert([{
                user_id: user.id,
                credits_limit: 100,
                credits_used: 0
              }]);
              
            if (simpleInsertError) {
              console.error("Simple insert error for vita credits:", simpleInsertError);
            } else {
              console.log("Vita credits created with simple insert");
            }
          } else {
            console.log("Vita credits created successfully");
          }
        } else {
          console.log("Existing vita credits found:", creditData);
          // Ensure the user has 100 credits if using free plan
          if (creditData.credits_limit < 100) {
            const { error: updateError } = await supabase
              .from('vita_credits')
              .update({ credits_limit: 100 })
              .eq('id', creditData.id);
              
            if (updateError) {
              console.error("Error updating vita credits limit:", updateError);
            } else {
              console.log("Updated vita credits limit to 100");
            }
          }
        }
      } catch (err) {
        console.error("Error in vita credits check:", err);
      }
    }
    
    checkProfile();
    ensureVitaCredits();
  }, [user, toast]);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 py-12 px-4">
        <div className="container mx-auto">
          <div className="mb-12 text-center">
            <h1 className="text-3xl font-display font-bold tracking-tight mb-3">
              Your Profile
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Manage your personal information and preferences to keep your wellness journey on track.
            </p>
          </div>
          
          {loading ? (
            <div className="max-w-3xl mx-auto space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : (
            <>
              {!user ? (
                <div className="text-center p-8 glass-card max-w-lg mx-auto">
                  <p>Please log in to view and manage your profile.</p>
                </div>
              ) : (
                <ProfileForm />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
